import type { ReactNode } from "react";

import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * The Close X is 28px: `SheetContent` draws it (a primitive this file does not
 * edit) with `p-1.5` round a 16px icon. A screen worked with a thumb, such as
 * receiving a returned parcel (#529), opts in to a 44px one on a phone and on
 * any touch screen, by styling the header's first button from here.
 */
/*
  Written out in full, never assembled from parts: Tailwind finds a class by
  reading it as a literal in the source, and one built at runtime gets no CSS.
*/
const LARGE_CLOSE =
  "max-sm:[&>div:first-child>button:first-child]:flex max-sm:[&>div:first-child>button:first-child]:size-11 max-sm:[&>div:first-child>button:first-child]:items-center max-sm:[&>div:first-child>button:first-child]:justify-center pointer-coarse:[&>div:first-child>button:first-child]:flex pointer-coarse:[&>div:first-child>button:first-child]:size-11 pointer-coarse:[&>div:first-child>button:first-child]:items-center pointer-coarse:[&>div:first-child>button:first-child]:justify-center";

/**
 * A record over the list that found it.
 *
 * Sized to the content, not to a fraction of the window. `OrderDetail` caps
 * itself at `max-w-3xl` — 768px, a readable measure for a stacked detail —
 * and the other detail screens do the same, so a panel wider than that is
 * empty on the right however impressive the percentage sounds. 860px covers
 * the measure plus the header's own padding.
 *
 * That the list stays readable beside it is a consequence rather than the
 * goal, but a welcome one: 580px of table on a 1440px screen is enough to
 * pick the next row without closing the panel, which is how a queue gets
 * worked. Sizing to a large fraction of the window would have bought dead
 * space and given that up.
 *
 * `max-w-[92vw]` so a narrow window keeps a dismiss target, and full width
 * below `sm` where a strip would be pure loss.
 */
export function RecordDrawer({
  title,
  description,
  actions,
  wide = false,
  largeClose = false,
  children,
}: {
  /**
   * The record's own name — the order number, the person, the product.
   *
   * It is the panel's accessible name and its first line, so the record type
   * alone says nothing the tab strip did not. Callers look the row up on the
   * page they are showing and fall back to the type: a link opened cold lands
   * on a record the current page does not contain, and blocking the panel on
   * a lookup of its own would show an empty drawer for as long as that took.
   */
  title: string;
  description?: string;
  actions?: ReactNode;
  /**
   * An order (#644): about 78% of the window on a desktop, so the list stays
   * in view beside it and the arrow keys can step through its rows, and the
   * whole width on a phone.
   */
  wide?: boolean;
  /** A 44px Close on a phone and on touch screens. Opt-in: see `LARGE_CLOSE`. */
  largeClose?: boolean;
  children: ReactNode;
}) {
  const { recordId, close } = useOpenRecord();

  return (
    <Sheet
      open={recordId !== null}
      onOpenChange={(next) => {
        // Radix reports Escape, the overlay and the close button through this
        // one channel, so the URL is updated in one place for all three.
        if (!next) close();
      }}
    >
      <SheetContent
        title={title}
        description={description}
        // The page header's Feedback button is under the drawer's overlay, so
        // the drawer carries its own (#498). Last, so the page's own actions
        // keep the place they have always had.
        actions={
          <>
            {actions}
            <FeedbackButton />
          </>
        }
        className={cn(
          wide
            ? "w-full sm:w-[78vw] sm:max-w-[78vw]"
            : "w-full sm:w-[860px] sm:max-w-[92vw]",
          largeClose && LARGE_CLOSE,
        )}
      >
        {children}
      </SheetContent>
    </Sheet>
  );
}
